import { Payload, Platform } from "@/types/profile";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, Plus, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface PayloadCardProps {
  payload: Payload;
  selectedPlatforms: Platform[];
  isSelected: boolean;
  isSupported: boolean;
  onSelect: (payloadId: string) => void;
}

export const PayloadCard = ({
  payload,
  selectedPlatforms,
  isSelected,
  isSupported,
  onSelect,
}: PayloadCardProps) => {
  const getPlatformLabel = (platform: Platform): string => {
    const platformLabels: Record<Platform, string> = {
      macos: "macOS",
      ios: "iOS", 
      ipados: "iPadOS",
      tvos: "tvOS"
    };
    return platformLabels[platform];
  };

  const getComplexityLevel = (configFieldsCount: number): string => {
    if (configFieldsCount === 0) return "Simple";
    if (configFieldsCount <= 3) return "Medium";
    return "Advanced";
  };

  const getComplexityColor = (configFieldsCount: number): string => {
    if (configFieldsCount === 0) return "bg-green-100 text-green-800";
    if (configFieldsCount <= 3) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  return (
    <GlassCard 
      variant="subtle"
      className={cn(
        "cursor-pointer",
        isSelected && "ring-2 ring-primary",
        !isSupported && "opacity-50 cursor-not-allowed"
      )}
      onClick={() => isSupported && onSelect(payload.id)}
    >
      <GlassCardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <GlassCardTitle className="text-sm font-medium flex items-center gap-2">
              {payload.name}
              {payload.configFields.length > 0 && (
                <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
              )}
            </GlassCardTitle>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {payload.description}
            </p>
          </div>
          <Button
            variant={isSelected ? "default" : "outline"}
            size="sm"
            className="ml-2 h-8 w-8 p-0"
            disabled={!isSupported}
          >
            {isSelected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      </GlassCardHeader>
      <GlassCardContent className="pt-0">
        <div className="space-y-2">
          {/* Platform badges */}
          <div className="flex flex-wrap gap-1">
            {payload.supportedPlatforms.map(platform => (
              <Badge
                key={platform}
                variant={selectedPlatforms.includes(platform) ? "default" : "outline"}
                className="text-xs px-2 py-0.5"
              >
                {getPlatformLabel(platform)}
              </Badge>
            ))}
          </div>
          
          {/* Configuration info */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {payload.configFields.length} config{payload.configFields.length !== 1 ? 's' : ''}
            </span>
            <Badge 
              className={cn(
                "text-xs px-2 py-0.5",
                getComplexityColor(payload.configFields.length)
              )}
              variant="outline"
            >
              {getComplexityLevel(payload.configFields.length)}
            </Badge>
          </div>
          
          {/* Category */}
          <div className="text-xs text-muted-foreground">
            {payload.category}
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
};